import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi, type OrgRequest } from '@/lib/api';
import { useMe } from '@/lib/me';
import { Badge, Button, Card, PageHeader } from '@/components/ui';
import { OrgForm } from '@/components/org-form';
import { OrgRequestForm } from '@/components/org-request-form';

/**
 * Rare, one-off: people with creator rights create an organization here;
 * everyone else requests one for the Nirvik team to review.
 */
export function NewOrganizationPage() {
  const me = useMe();
  if (me.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const c = me.data?.creator;
  const canCreate = Boolean(c && (c.unlimited || c.used < c.maxOrgs));
  return canCreate ? <CreateOrganization /> : <RequestOrganization quotaUsed={Boolean(c)} />;
}

function CreateOrganization() {
  const api = useApi();
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: api.createOrg,
    onSuccess: (org) => {
      queryClient.invalidateQueries({ queryKey: ['myOrgs'] });
      queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate(`/organizations/${org.id}`);
    },
  });
  const c = me.data?.creator;
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Create an organization"
        description="You'll be its owner. Next you can invite members, verify your email domain and add child organizations."
        back={{ to: '/home', label: 'Home' }}
        action={
          c &&
          !c.unlimited && (
            <Badge>
              {c.used} of {c.maxOrgs} used
            </Badge>
          )
        }
      />
      <Card>
        <OrgForm
          idPrefix="new-org"
          submitLabel="Create organization"
          pending={create.isPending}
          error={create.error}
          onSubmit={(input) => create.mutate(input)}
        />
      </Card>
    </div>
  );
}

const STATUS_LABEL: Record<OrgRequest['status'], string> = {
  pending: 'Waiting for review',
  approved: 'Approved',
  rejected: 'Not approved',
  canceled: 'Canceled',
};

function RequestOrganization({ quotaUsed }: { quotaUsed: boolean }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const requests = useQuery({ queryKey: ['myOrgRequests'], queryFn: api.myOrgRequests });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['myOrgRequests'] });
  const submit = useMutation({ mutationFn: api.requestOrg, onSuccess: refresh });
  const cancel = useMutation({ mutationFn: api.cancelOrgRequest, onSuccess: refresh });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Request an organization account"
        description={
          quotaUsed
            ? "You've used all the organizations you can create. Request another and the Nirvik team will review it."
            : 'Organizations are set up by the Nirvik team. Tell us about yours; we usually reply within one working day, by email.'
        }
        back={{ to: '/home', label: 'Home' }}
      />

      {requests.data?.requests.length ? (
        <Card title="Your requests">
          <ul className="divide-y divide-border">
            {requests.data.requests.map((r) => (
              <li
                key={r.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"
              >
                <div>
                  <p className="font-medium">{r.name}</p>
                  {r.decisionNote && (
                    <p className="text-muted-foreground">Note: {r.decisionNote}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge>{STATUS_LABEL[r.status]}</Badge>
                  {r.status === 'approved' && r.orgId && (
                    <Link to={`/organizations/${r.orgId}`} className="text-primary">
                      Open
                    </Link>
                  )}
                  {r.status === 'pending' && (
                    <Button
                      variant="ghost"
                      disabled={cancel.isPending}
                      onClick={() => cancel.mutate(r.id)}
                    >
                      Cancel request
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card>
        {submit.isSuccess ? (
          <div className="space-y-3 text-sm">
            <p className="font-medium">Request sent</p>
            <p className="text-muted-foreground">
              We'll email you when it's reviewed. You can track it above.
            </p>
            <Button variant="secondary" onClick={() => submit.reset()}>
              Request another organization
            </Button>
          </div>
        ) : (
          <OrgRequestForm
            pending={submit.isPending}
            error={submit.error}
            onSubmit={(input) => submit.mutate(input)}
          />
        )}
      </Card>
    </div>
  );
}
